import { type SupportedLocale } from '@zep-us/i18n';

import { type BoardTypeValue } from '@/shared/features/board/utils/boardTypeLabel';

/**
 * 이미지·샘플보드가 실제로 준비된 로케일. 그 외 로케일은 전부 en 으로 떨어진다.
 * (배열이 아니라 Record 키로 두면 아래 맵들과 누락이 같이 잡힌다)
 */
const FALLBACK_LOCALE = 'en';

type SupportedContentLocale = 'ko' | 'ja' | 'id' | 'th' | 'en';

const CONTENT_LOCALES: readonly SupportedContentLocale[] = [
  'ko',
  'ja',
  'id',
  'th',
  'en',
];

function resolveLocale(locale: SupportedLocale): SupportedContentLocale {
  return CONTENT_LOCALES.includes(locale as SupportedContentLocale)
    ? (locale as SupportedContentLocale)
    : FALLBACK_LOCALE;
}

// ─── 샘플 보드 ────────────────────────────────────────────────────────────────

/**
 * 샘플 보드는 prod(quiz.zep.us)의 실 데이터다.
 *
 * 내부 라우팅(`/board/<id>`)이 아니라 prod 절대 URL 로 열고 새 창으로 띄운다. 내부
 * 라우팅으로 보내면 dev/preview 배포에서는 그 환경 DB 에 없는 보드라 404 가 되고,
 * 시안 검수 때마다 샘플이 죽어 보인다.
 */
const SAMPLE_BOARD_ORIGIN = 'https://quiz.zep.us';

const SAMPLE_BOARD_IDS: Record<
  SupportedContentLocale,
  Record<BoardTypeValue, string>
> = {
  ko: {
    WALL: '6aa7e68335ccbc100306fb5f',
    GROUP: '6aa7e6b40687f8e643ab08d1',
    TIMELINE: '6aa7e6630687f8e643aa6af3',
  },
  ja: {
    WALL: '6aa7e6c335ccbc10030782ad',
    GROUP: '6aa7e6c90687f8e643ab3534',
    TIMELINE: '6aa7e6bb0687f8e643ab176a',
  },
  id: {
    WALL: '6aa7e6da0687f8e643ab6245',
    GROUP: '6aa7e6e035ccbc100307c57b',
    TIMELINE: '6aa7e6d135ccbc1003079e82',
  },
  th: {
    WALL: '6aa7e6f00687f8e643ab9bac',
    GROUP: '6aa7e6f535ccbc100307f46b',
    TIMELINE: '6aa7e6e635ccbc100307d553',
  },
  en: {
    WALL: '6aa7e7060687f8e643abd36a',
    GROUP: '6aa7e70b0687f8e643abdfb8',
    TIMELINE: '6aa7e6fc0687f8e643abbba6',
  },
};

export function getSampleBoardUrl(
  locale: SupportedLocale,
  boardType: BoardTypeValue,
): string {
  return `${SAMPLE_BOARD_ORIGIN}/board/${
    SAMPLE_BOARD_IDS[resolveLocale(locale)][boardType]
  }`;
}

// ─── 이미지 ──────────────────────────────────────────────────────────────────

const ASSET_ROOT = '/assets/board-landing';

const SAMPLE_IMAGE_DIR: Record<BoardTypeValue, string> = {
  WALL: 'wall',
  GROUP: 'group',
  TIMELINE: 'timeline',
};

export function getHeroImage(locale: SupportedLocale): string {
  return `${ASSET_ROOT}/hero/${resolveLocale(locale)}.webp`;
}

export function getSampleImage(
  locale: SupportedLocale,
  boardType: BoardTypeValue,
): string {
  return `${ASSET_ROOT}/samples/${SAMPLE_IMAGE_DIR[boardType]}/${resolveLocale(
    locale,
  )}.webp`;
}

export type FeatureImageKey = 'collab' | 'unlimited';

export function getFeatureImage(
  locale: SupportedLocale,
  key: FeatureImageKey,
): string {
  return `${ASSET_ROOT}/features/${key}/${resolveLocale(locale)}.webp`;
}

/** QR·기기 목업이라 텍스트가 없어 로케일 분기가 없다. */
export const FEATURE_QR_IMAGE = `${ASSET_ROOT}/features/qr.webp`;
export const INTRO_BOARD_IMAGE = `${ASSET_ROOT}/intro-board.webp`;
export const BOTTOM_CTA_IMAGE = `${ASSET_ROOT}/bottom-cta.webp`;

// ─── 링크 ────────────────────────────────────────────────────────────────────

/**
 * 젭보드 가이드(채널톡 도움말) URL.
 *
 * 경로에서 로케일 세그먼트만 갈린다. ko·en·ja 만 문서가 준비돼 있어 나머지
 * 로케일은 이미지·샘플보드와 같은 규칙으로 en 으로 떨어진다.
 */
const GUIDE_LOCALE_SEGMENT: Record<SupportedContentLocale, 'ko' | 'en' | 'ja'> =
  {
    ko: 'ko',
    ja: 'ja',
    en: 'en',
    id: 'en',
    th: 'en',
  };

export function getBoardGuideUrl(locale: SupportedLocale): string {
  const segment = GUIDE_LOCALE_SEGMENT[resolveLocale(locale)];

  return `https://docs.channel.io/zepquiz/${segment}/categories/%EB%B3%B4%EB%93%9C-%EC%A0%9C%EC%9E%91%ED%95%98%EA%B8%B0-72df5dff`;
}

/** CTA 목적지 — 보드 생성 페이지. */
export const BOARD_CREATE_PATH = '/board/new';

// ─── 레이아웃 ────────────────────────────────────────────────────────────────

/**
 * Intro·Stats·Samples·Features·Faq·BottomCta 6개 섹션이 공유하는 좌우·상하
 * padding. Hero 는 이 상수를 쓰지 않는다 — Figma 상 상단 64px·하단 80px 로
 * 비대칭이라(CTA 버튼과 이미지 사이 여백), 대칭인 이 상수를 씌우면 Hero
 * 쪽에 override 가 필요해져 오히려 더 지저분해진다.
 */
export const SECTION_PADDING =
  'px-[16px] py-2xl min-[751px]:px-[24px] min-[1025px]:px-[40px] min-[1025px]:py-3xl';
